import { useQuery } from '@tanstack/react-query';
import { isFirebaseConfigured } from '@/firebase';
import { GUEST_DAILY_LIMIT, USER_DAILY_LIMIT } from '@/constants/limits';
import { fetchStorageUsedBytes } from '@/services/downloadService';
import { useAuthStore } from '@/store/authStore';
import { useDownloadStore } from '@/store/downloadStore';
import type { Platform } from '@/types';

export interface DashboardStats {
  downloadsToday: number;
  totalDownloads: number;
  storageUsedBytes: number | null;
  topPlatform: Platform | null;
  dailyLimit: number;
  isGuest: boolean;
}

function topKey(counts: Partial<Record<Platform, number>>): Platform | null {
  let best: Platform | null = null;
  let bestCount = 0;
  for (const [platform, count] of Object.entries(counts) as [Platform, number][]) {
    if (count > bestCount) {
      best = platform;
      bestCount = count;
    }
  }
  return best;
}

export function useStats(): DashboardStats {
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const guestHistory = useDownloadStore((s) => s.guestHistory);

  const storageQuery = useQuery({
    queryKey: ['stats', 'storage', user?.uid],
    enabled: Boolean(user) && isFirebaseConfigured,
    queryFn: () => fetchStorageUsedBytes(user!.uid),
    staleTime: 60_000,
  });

  if (user) {
    return {
      downloadsToday: profile?.downloadsToday ?? 0,
      totalDownloads: profile?.totalDownloads ?? 0,
      storageUsedBytes: storageQuery.data ?? null,
      topPlatform: profile ? topKey(profile.platformCounts) : null,
      dailyLimit: USER_DAILY_LIMIT,
      isGuest: false,
    };
  }

  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const now = Date.now();

  const counts: Partial<Record<Platform, number>> = {};
  let storage = 0;
  let today = 0;
  for (const record of guestHistory) {
    counts[record.platform] = (counts[record.platform] ?? 0) + 1;
    if (record.createdAt >= startOfDay.getTime()) today += 1;
    if (record.status === 'completed' && (record.expiresAt ?? 0) > now) {
      storage += record.fileSize ?? 0;
    }
  }

  return {
    downloadsToday: today,
    totalDownloads: guestHistory.length,
    storageUsedBytes: storage,
    topPlatform: topKey(counts),
    dailyLimit: GUEST_DAILY_LIMIT,
    isGuest: true,
  };
}
